import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  UserIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { getThemeStyles } from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import OrganizationManagement from './OrganizationManagement';
import { isFeatureEnabled } from '../../config/environment';
import WhiteLabelSettings from './WhiteLabelSettings';
import { ServiceFactory } from '../../services/factory/ServiceFactory';
import type { User } from '../../services/interfaces/IApiService';

// API URL configuration - using ADMIN API URL from env, fallback to regular API URL
const API_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || 'https://3ein5nfb8k.execute-api.us-east-2.amazonaws.com/prod';

interface Employer {
  id: string;
  name: string;
  brokerName?: string;
  brokerId?: string;
}

interface Broker {
  id: string;
  name: string;
  agency?: string;
  employers?: Employer[];
}

interface TPA {
  id: string;
  name: string;
  brokers?: Broker[];
}

// User interface imported from IApiService

interface NewUser {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string; // Keep for backward compatibility, will be computed from firstName + lastName
  role: string;
  brokerId?: string;
  tpaId?: string;
  employerId?: string;
  agency?: string;
  tempPassword: string;
  phoneNumber?: string;
}

interface NewTPA {
  id: string;
  name: string;
}

// Add props interface
interface AdminPanelProps {
  // No props needed since we're removing tabs
}

const AdminPanel: React.FC<AdminPanelProps> = () => {
  const { user, getIdToken } = useAuth();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const theme = getThemeStyles(isDarkMode);

  // Initialize API service using ServiceFactory
  const apiService = ServiceFactory.createApiService();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tpa, setTpa] = useState<TPA | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'organizations'>('users');
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  
  // New state for user management
  const [newUser, setNewUser] = useState<NewUser>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    name: '',
    role: 'broker',
    brokerId: '',
    tempPassword: '',
    phoneNumber: ''
  });
  
  // Add state for real broker users from Cognito
  const [brokerUsers, setBrokerUsers] = useState<User[]>([]);
  const [loadingBrokers, setLoadingBrokers] = useState(false);
  
  // Add state for editing users
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  
  // Add state for the list of users
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Add state for employer users
  const [employerUsers, setEmployerUsers] = useState<User[]>([]);
  const [loadingEmployers, setLoadingEmployers] = useState(false);

  // TPA management state
  const [tpaList, setTpaList] = useState<TPA[]>([]);
  const [tpaDialogOpen, setTpaDialogOpen] = useState(false);
  const [newTPA, setNewTPA] = useState<NewTPA>({ id: '', name: '' });
  const [loadingTpas, setLoadingTpas] = useState(false);

  // Add state for view user dialog
  const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState<User | null>(null);

  // Password visibility and validation state
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Password validation function
  const validatePassword = (password: string): boolean => {
    // Password should be at least 8 characters with numbers, special chars, uppercase and lowercase
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    }
    
    if (!passwordRegex.test(password)) {
      setPasswordError('Password must include uppercase, lowercase, number, and special character');
      return false;
    }
    
    setPasswordError(null);
    return true;
  };

  // Handle password change with validation
  const handlePasswordChange = (password: string) => {
    setNewUser({...newUser, tempPassword: password});
    if (password) {
      validatePassword(password);
    } else {
      setPasswordError(null);
    }
  };

  // Handle name changes and compute full name
  const handleNameChange = (field: 'firstName' | 'lastName', value: string) => {
    const updatedUser = { ...newUser, [field]: value };
    // Compute full name from firstName and lastName
    updatedUser.name = `${updatedUser.firstName} ${updatedUser.lastName}`.trim();
    setNewUser(updatedUser);
  };

  // Extract fetchUsers function to make it available to handleDeleteUser
  const fetchUsers = async () => {
    if (!user) return;

    try {
      setLoadingUsers(true);
      console.log('AdminPanel: Fetching users via ServiceFactory...');

      // Use ServiceFactory to get users, passing tpaId if available
      const fetchedUsers = await apiService.Users.getUsers(user.tpaId);
      console.log('AdminPanel: Users data received:', fetchedUsers);

      // Set users in state - ensure it's always an array
      setUsers(Array.isArray(fetchedUsers) ? fetchedUsers : []);

      // Reset error
      setError(null);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(`Error fetching users: ${err.message}`);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch users with TPA-based filtering
  useEffect(() => {
    fetchUsers();
    fetchBrokerUsers();
    
    // Only fetch TPA groups for global admin users (not tpa_admin)
    if (user?.role === 'admin') {
      fetchTpaGroups();
    }
  }, [user]);

  // Check if user is in admin group
  useEffect(() => {
    // Set admin status based on user role
    if (user) {
      setIsAdmin(user.role === 'admin' || user.role === 'tpa_admin' || user.role === 'tpa_user');
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  // Fetch TPA data
  useEffect(() => {
    const fetchTpa = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        if (!API_URL) {
          setError("API URL not configured. Please check your environment variables.");
          setLoading(false);
          return;
        }
        
        const token = await getIdToken();
        if (!token) {
          throw new Error("Authentication token not available");
        }
        
        console.log(`AdminPanel: Connecting to API: ${API_URL}`);
        
        try {
          // For TPA admins, we only want to fetch their specific TPA data
          // For global admins, we fetch all TPA data
          let tpaEndpoint = `${API_URL}/api/tpa`;
          
          // Only append the TPA ID if it exists and we're not an admin
          if (user.role !== 'admin' && user.tpaId) {
            console.log(`AdminPanel: User has tpaId: ${user.tpaId}`);
            // Ensure we're not appending tpa_ prefix if it's already there
            // Use query parameter instead of path parameter
            tpaEndpoint = `${API_URL}/api/tpa?id=${user.tpaId}`;
          }
             
          console.log(`AdminPanel: Fetching TPA data from ${tpaEndpoint}`);
          
          // Make sure there's no extra space in the Authorization header
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token.trim()}`
          };
          
          const response = await fetch(tpaEndpoint, {
            method: 'GET',
            headers: headers
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`AdminPanel: API error (${response.status}): ${errorText}`);
            throw new Error(`API error: ${response.status}`);
          }
          
          const data = await response.json();
          console.log("AdminPanel: TPA data received:", data);
          
          // Debug log to check brokers
          if (data && data.brokers) {
            console.log(`AdminPanel: Found ${data.brokers.length} brokers in TPA data`);
            data.brokers.forEach((broker: any, index: number) => {
              console.log(`AdminPanel: Broker ${index + 1}:`, broker);
              if (broker.employers) {
                console.log(`AdminPanel: Broker ${index + 1} has ${broker.employers.length} employers`);
              } else {
                console.log(`AdminPanel: Broker ${index + 1} has no employers`);
              }
            });
          } else {
            console.warn("AdminPanel: No brokers found in TPA data:", data);
          }
          
          setTpa(data);
          setError(null);
        } catch (fetchError: any) {
          if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('Network request failed')) {
            console.error('AdminPanel: API connection error:', fetchError);
            setError(`Could not connect to API at ${API_URL}. The API might not be running or might be deployed to a different endpoint.`);
          } else {
            console.error('AdminPanel: API error:', fetchError);
            setError(fetchError.message || "Failed to fetch TPA data");
          }
        }
      } catch (err: any) {
        console.error('Error in TPA data fetch process:', err);
        setError(err.message || "Failed to fetch TPA data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchTpa();
  }, [user, getIdToken]);

  // New function to fetch real broker users
  const fetchBrokerUsers = async () => {
    if (!user) return;
    
    try {
      setLoadingBrokers(true);
      
      if (!API_URL) {
        console.error("API URL not configured. Please check your environment variables.");
        setLoadingBrokers(false);
        return;
      }
      
      const token = await getIdToken();
      if (!token) {
        console.error("Authentication token not available");
        setLoadingBrokers(false);
        return;
      }
      
      // Debug logging
      console.log('AdminPanel: Fetching broker users...');
      
      // Use the endpoint path that Lambda expects with a filter for brokers
      let endpoint = `${API_URL}/api/users?role=broker`;
      
      // Always include tpaId parameter to get brokers from the current TPA
      if (user.tpaId) {
        endpoint = `${endpoint}&tpaId=${user.tpaId}`;
      }
      
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      endpoint = `${endpoint}&_t=${timestamp}`;
      
      console.log(`AdminPanel: Fetching broker users from ${endpoint}`);
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token.trim()}`
      };
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`AdminPanel: API error fetching brokers (${response.status}): ${errorText}`);
        setLoadingBrokers(false);
        return;
      }
      
      const data = await response.json();
      console.log('AdminPanel: Broker users data received:', data);
      
      // Set broker users in state
      if (data.users && Array.isArray(data.users)) {
        // Filter to only include users with broker role
        const brokers = data.users.filter((u: User) => u.role === 'broker');
        console.log(`AdminPanel: Found ${brokers.length} broker users`);
        setBrokerUsers(brokers);
      } else {
        console.warn('AdminPanel: No broker users found or invalid data format');
        setBrokerUsers([]);
      }
    } catch (err: any) {
      console.error('Error fetching broker users:', err);
    } finally {
      setLoadingBrokers(false);
    }
  };

  // New function to fetch employers for a selected broker
  const fetchEmployersForBroker = async (brokerId: string) => {
    if (!user || !brokerId) return;
    
    try {
      setLoadingEmployers(true);
      
      if (!API_URL) {
        console.error("API URL not configured. Please check your environment variables.");
        setLoadingEmployers(false);
        return;
      }
      
      const token = await getIdToken();
      if (!token) {
        console.error("Authentication token not available");
        setLoadingEmployers(false);
        return;
      }
      
      console.log(`AdminPanel: Fetching employers for broker: ${brokerId}`);
      
      // Use the endpoint path that Lambda expects with a filter for employers
      let endpoint = `${API_URL}/api/users?role=employer&brokerId=${encodeURIComponent(brokerId)}`;
      
      // Always include tpaId parameter to get employers from the current TPA
      if (user.tpaId) {
        endpoint = `${endpoint}&tpaId=${user.tpaId}`;
      }
      
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      endpoint = `${endpoint}&_t=${timestamp}`;
      
      console.log(`AdminPanel: Fetching employer users from ${endpoint}`);
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token.trim()}`
      };
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`AdminPanel: API error fetching employers (${response.status}): ${errorText}`);
        setLoadingEmployers(false);
        return;
      }
      
      const data = await response.json();
      console.log('AdminPanel: Employer users data received:', data);
      
      // Set employer users in state
      if (data.users && Array.isArray(data.users)) {
        // Filter to only include users with employer role
        const employers = data.users.filter((u: User) => u.role === 'employer' && u.brokerId === brokerId);
        console.log(`AdminPanel: Found ${employers.length} employer users for broker ${brokerId}`);
        setEmployerUsers(employers);
      } else {
        console.warn('AdminPanel: No employer users found or invalid data format');
        setEmployerUsers([]);
      }
    } catch (err: any) {
      console.error('Error fetching employer users:', err);
    } finally {
      setLoadingEmployers(false);
    }
  };

  // Add handler for broker selection change
  const handleBrokerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedBrokerId = e.target.value;
    
    // Update the form data
    setNewUser(prev => ({
      ...prev,
      brokerId: selectedBrokerId,
      employerId: '' // Reset employer selection when broker changes
    }));
    
    // Fetch employers for this broker if a broker is selected
    if (selectedBrokerId) {
      fetchEmployersForBroker(selectedBrokerId);
    } else {
      // Clear employers if no broker selected
      setEmployerUsers([]);
    }
  };

  // Handle adding a new user - modified to ensure role-based restrictions
  const handleAddUser = async () => {
    try {
      // Validate form fields
      if (!newUser.email.trim()) {
        setError('Email is required');
        return;
      }
      
      if (!newUser.firstName.trim()) {
        setError('First name is required');
        return;
      }
      
      if (!newUser.lastName.trim()) {
        setError('Last name is required');
        return;
      }
      
      if (!newUser.tempPassword.trim()) {
        setError('Temporary password is required');
        return;
      }
      
      // Validate password strength
      if (!validatePassword(newUser.tempPassword)) {
        setError(passwordError || 'Password does not meet requirements');
        return;
      }
      
      // Role-specific validations and RBAC restrictions
      if (user?.role !== 'admin') {
        // TPA admins can only create TPA users, broker and employer users under their TPA
        if (user?.role === 'tpa_admin') {
          if (!['tpa_admin', 'tpa_user', 'broker', 'employer'].includes(newUser.role)) {
            setError('TPA admins can only create TPA users, broker and employer users');
            return;
          }
          
          // Enforce tpaId for users created by TPA admin
          newUser.tpaId = user.tpaId;
        }
        
        // Employer users cannot create users
        if (user?.role === 'employer') {
          setError('Employers do not have permission to create users');
          return;
        }
      }
      
      // Additional role-specific validations
      if (['tpa_admin', 'tpa_user', 'broker'].includes(newUser.role) && !newUser.tpaId) {
        setError('Please select a TPA for TPA and broker users');
        return;
      }
      
      if (newUser.role === 'employer' && !newUser.brokerId) {
        setError('Please select a broker for employer users');
        return;
      }

      // Create user request with proper hierarchy
      const userData: any = {
        username: newUser.email,
        email: newUser.email,
        name: newUser.name,
        role: newUser.role,
        tempPassword: newUser.tempPassword,
        phoneNumber: newUser.phoneNumber
      };

      // Add organization IDs based on role and permissions
      if (newUser.tpaId) {
        userData.tpaId = newUser.tpaId;
      } else if (user?.tpaId) {
        // Always include TPA ID from current user if available
        userData.tpaId = user.tpaId;
      }

      // For TPA-level users (tpa_admin, tpa_user), don't set broker or employer IDs
      if (['tpa_admin', 'tpa_user'].includes(newUser.role)) {
        // TPA-level users only need the TPA ID, no broker or employer associations
        console.log(`Creating TPA-level user (${newUser.role}): ${newUser.email}`);
      }
      // For broker users, set broker_id to the username
      else if (newUser.role === 'broker') {
        userData.brokerId = newUser.email;
        console.log(`Setting broker_id to username for broker user: ${newUser.email}`);
      }
      // For employer users, set employer_id to the username and associate with broker
      else if (newUser.role === 'employer') {
        // For employer users, set employer_id to the username
        userData.employerId = newUser.email;
        console.log(`Setting employer_id to username for employer user: ${newUser.email}`);

        if (newUser.brokerId) {
          // Using broker username (Cognito user) as the ID now
          userData.brokerId = newUser.brokerId;

          // Find the selected broker to get additional details if needed
          const selectedBroker = brokerUsers.find(b => b.username === newUser.brokerId);
          if (selectedBroker) {
            console.log(`Selected broker: ${selectedBroker.name} (${selectedBroker.username})`);
            // Include broker name for reference
            userData.brokerName = selectedBroker.name || selectedBroker.username;
          }
        }
      }

      console.log('AdminPanel: Creating user with data:', userData);

      // Use ServiceFactory to create user
      const result = await apiService.Users.createUser(userData);
      console.log('AdminPanel: User created successfully:', result);
      
      // Reset form and close dialog
      setNewUser({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        name: '',
        role: 'broker',
        brokerId: '',
        tempPassword: '',
        phoneNumber: ''
      });
      
      setUserDialogOpen(false);
      setError(null);
      
      // Immediately refresh the users list to show the new user
      console.log('Refreshing users list after creating new user');
      await fetchUsers();
      
      // Refresh broker users list if we created a broker
      // This ensures the new broker shows up immediately in the dropdown
      if (userData.role === 'broker') {
        console.log('Refreshing broker users after creating new broker');
        await fetchBrokerUsers();
      }
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.message || 'Failed to create user');
    }
  };

  // Handle deleting a user
  const handleDeleteUser = async (username: string) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }

    try {
      console.log('AdminPanel: Deleting user via ServiceFactory:', username);

      // Use ServiceFactory to delete user
      await apiService.Users.deleteUser(username);

      // Refresh users data
      await fetchUsers();
      setError(null);

      // Show success message
      alert(`User ${username} deleted successfully`);
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.message || "Failed to delete user");
    }
  };

  // Add function to handle sorting
  const handleSort = (field: string) => {
    // If clicking on the same field, toggle direction
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking on a new field, set it as the sort field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Add function to filter and sort users
  const getFilteredAndSortedUsers = () => {
    // Ensure users is always an array
    const userArray = Array.isArray(users) ? users : [];

    // First filter users based on search term
    const filteredUsers = userArray.filter(user => {
      // Apply search term filter
      const matchesSearch = !searchTerm.trim() || (() => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (user.username && user.username.toLowerCase().includes(searchLower)) ||
          (user.name && user.name.toLowerCase().includes(searchLower)) ||
          (user.email && user.email.toLowerCase().includes(searchLower)) ||
          (user.role && user.role.toLowerCase().includes(searchLower)) ||
          (user.phoneNumber && user.phoneNumber.toLowerCase().includes(searchLower))
        );
      })();
      
      return matchesSearch;
    });
    
    // Then sort the filtered users
    return filteredUsers.sort((a, b) => {
      let aValue = a[sortField as keyof User] || '';
      let bValue = b[sortField as keyof User] || '';
      
      // Special case for names, split into first and last
      if (sortField === 'firstName') {
        aValue = a.name ? a.name.split(' ')[0] : '';
        bValue = b.name ? b.name.split(' ')[0] : '';
      } else if (sortField === 'lastName') {
        aValue = a.name ? a.name.split(' ').slice(1).join(' ') : '';
        bValue = b.name ? b.name.split(' ').slice(1).join(' ') : '';
      }
      
      // Convert to strings for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      // Compare based on direction
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };



  // Render the users tab with table layout
  const renderUsers = () => {
    const isAdmin = user?.role === 'admin';
    const isTpaAdmin = user?.role === 'tpa_admin';
    
    // Get filtered and sorted users
    const filteredSortedUsers = getFilteredAndSortedUsers();
    
    return (
      <div className="bg-white dark:bg-night-800 rounded-brand shadow-brand dark:shadow-dark overflow-hidden">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-night dark:text-white">User management</h2>
            
            {/* Only show Add User button for admin and tpa_admin roles */}
            {(isAdmin || isTpaAdmin) && (
              <button
                onClick={() => setUserDialogOpen(true)}
                className="flex items-center text-sm px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                New
              </button>
            )}
          </div>
          
          {loadingUsers ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary-500 rounded-full border-t-transparent"></div>
              <span className="ml-3 text-night dark:text-white">Loading users...</span>
            </div>
          ) : error ? (
            <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search by name, email, role, etc."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="w-full overflow-x-auto">
                <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-night-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-night-700">
                    <tr>
                      <th scope="col" className="w-12 px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"></th>
                      <th 
                        scope="col" 
                        className="w-[10%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-primary-500"
                        onClick={() => handleSort('username')}
                      >
                        EMAIL
                        {sortField === 'username' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="w-[10%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-primary-500"
                        onClick={() => handleSort('firstName')}
                      >
                        FIRST
                        {sortField === 'firstName' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="w-[10%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-primary-500"
                        onClick={() => handleSort('lastName')}
                      >
                        LAST
                        {sortField === 'lastName' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="w-[10%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-primary-500 hidden sm:table-cell"
                        onClick={() => handleSort('phoneNumber')}
                      >
                        PHONE
                        {sortField === 'phoneNumber' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="w-[15%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-primary-500"
                        onClick={() => handleSort('email')}
                      >
                        E-MAIL
                        {sortField === 'email' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th 
                        scope="col" 
                        className="w-[8%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-primary-500"
                        onClick={() => handleSort('role')}
                      >
                        ROLE
                        {sortField === 'role' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </th>
                      <th scope="col" className="w-[10%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">STATUS</th>
                      <th scope="col" className="w-[20%] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-night-800 divide-y divide-gray-200 dark:divide-night-700">
                    {filteredSortedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-2 py-4 text-center text-gray-500 dark:text-gray-400">
                          {searchTerm ? "No users match your search" : "No users found"}
                        </td>
                      </tr>
                    ) : (
                      filteredSortedUsers.map((user) => {
                        // Split name into first and last name if available
                        const nameParts = user.name ? user.name.split(' ') : ['', ''];
                        const firstName = nameParts[0] || '';
                        const lastName = nameParts.slice(1).join(' ') || '';
                        
                        return (
                          <tr key={user.username} className="hover:bg-gray-50 dark:hover:bg-night-700">
                            <td className="px-2 py-2 whitespace-nowrap">
                              <div className="flex-shrink-0 h-8 w-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                                <UserIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                              </div>
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap text-sm text-night dark:text-white truncate" title={user.username}>{user.username}</td>
                            <td className="px-2 py-2 whitespace-nowrap text-sm text-night dark:text-white truncate" title={firstName}>{firstName}</td>
                            <td className="px-2 py-2 whitespace-nowrap text-sm text-night dark:text-white truncate" title={lastName}>{lastName}</td>
                            <td className="px-2 py-2 whitespace-nowrap text-sm text-night dark:text-white truncate hidden sm:table-cell" title={user.phoneNumber || '-'}>{user.phoneNumber || '-'}</td>
                            <td className="px-2 py-2 whitespace-nowrap text-sm text-night dark:text-white truncate" title={user.email}>{user.email}</td>
                            <td className="px-2 py-2 whitespace-nowrap text-sm text-night dark:text-white truncate" title={user.role}>{user.role}</td>
                            <td className="px-2 py-2 whitespace-nowrap text-sm text-night dark:text-white truncate hidden lg:table-cell">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.enabled === false ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'}`}>
                                {user.enabled === false ? 'Disabled' : 'Active'}
                              </span>
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap text-xs font-medium">
                              <div className="flex space-x-2">
                                <button 
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                  onClick={() => handleViewUser(user)}
                                >
                                  View
                                </button>
                                <button 
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                  onClick={() => handleEditUser(user)}
                                >
                                  Edit
                                </button>
                                <button 
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  onClick={() => handleDeleteUser(user.username)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 flex justify-between items-center">
                <div>
                  <select 
                    className="px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    defaultValue="10"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  1
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* User Dialog - update the role selection and broker field */}
        {userDialogOpen && (
          <div className="fixed inset-0 overflow-y-auto z-50 flex items-start justify-center p-4 bg-night-900/50 pt-8 pb-8">
            <div className="relative bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-lg w-full mx-auto my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-night-700">
                <h3 className="text-lg font-semibold text-night dark:text-white">Add New User</h3>
                <button 
                  onClick={() => setUserDialogOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Email</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => {
                        const email = e.target.value;
                        setNewUser({...newUser, email: email, username: email});
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      placeholder="email@example.com"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      This email will be used for login.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">First Name</label>
                      <input
                        type="text"
                        value={newUser.firstName}
                        onChange={(e) => handleNameChange('firstName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                        placeholder="First Name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">Last Name</label>
                      <input
                        type="text"
                        value={newUser.lastName}
                        onChange={(e) => handleNameChange('lastName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                        placeholder="Last Name"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={newUser.phoneNumber}
                      onChange={(e) => setNewUser({...newUser, phoneNumber: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      placeholder="Phone Number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Role</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    >
                      <option value="">Select Role</option>
                      {/* Admin can create all roles */}
                      {user?.role === 'admin' && (
                        <>
                          <option value="tpa_admin">TPA Admin</option>
                          <option value="tpa_user">TPA User</option>
                          <option value="broker">Broker</option>
                          <option value="employer">Employer</option>
                        </>
                      )}
                      {/* TPA Admin can create TPA users, broker and employer roles within their TPA */}
                      {user?.role === 'tpa_admin' && (
                        <>
                          <option value="tpa_admin">TPA Admin</option>
                          <option value="tpa_user">TPA User</option>
                          <option value="broker">Broker</option>
                          <option value="employer">Employer</option>
                        </>
                      )}
                    </select>
                  </div>
                  
                  {/* TPA selection for admin users creating TPA admin or broker users */}
                  {user?.role === 'admin' && (newUser.role === 'tpa_admin' || newUser.role === 'tpa_user' || newUser.role === 'broker') && (
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">TPA</label>
                      <select
                        value={newUser.tpaId}
                        onChange={(e) => setNewUser({...newUser, tpaId: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      >
                        <option value="">Select TPA</option>
                        {loadingTpas ? (
                          <option value="" disabled>Loading TPAs...</option>
                        ) : (
                          tpaList.map((tpaItem) => (
                            <option key={tpaItem.id} value={tpaItem.id}>
                              {tpaItem.name}
                            </option>
                          ))
                        )}
                      </select>
                      {tpaList.length === 0 && !loadingTpas && (
                        <p className="mt-1 text-xs text-red-500">
                          No TPA groups found. Please create a TPA group first.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Show current TPA for tpa_admin users creating any role */}
                  {user?.role === 'tpa_admin' && (newUser.role === 'tpa_admin' || newUser.role === 'tpa_user' || newUser.role === 'broker' || newUser.role === 'employer') && (
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">TPA</label>
                      <input
                        type="text"
                        value={(user as any).tpaName || user.tpaId || 'Current TPA'}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Users will be created under your TPA.
                      </p>
                    </div>
                  )}
                  
                  {/* Broker selection for employer users */}
                  {(newUser.role === 'employer') && (
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">Broker</label>
                      <select
                        value={newUser.brokerId}
                        onChange={handleBrokerChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      >
                        <option value="">Select Broker</option>
                        {loadingBrokers ? (
                          <option value="" disabled>Loading brokers...</option>
                        ) : (
                          brokerUsers.map((broker) => (
                            <option key={broker.username} value={broker.username}>
                              {broker.name || broker.username}
                            </option>
                          ))
                        )}
                      </select>
                      {brokerUsers.length === 0 && !loadingBrokers && (
                        <p className="mt-1 text-xs text-red-500">
                          No broker users found. Please create broker users first.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Agency field for broker users */}
                  {newUser.role === 'broker' && (
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">Agency</label>
                      <input
                        type="text"
                        value={newUser.agency || ''}
                        onChange={(e) => setNewUser({...newUser, agency: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                        placeholder="Agency Name (optional)"
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Temporary Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newUser.tempPassword}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        className={`w-full px-3 py-2 pr-10 border ${passwordError ? 'border-red-500' : 'border-gray-300 dark:border-night-600'} rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white`}
                        placeholder="Temporary Password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-white"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {passwordError && (
                      <p className="mt-1 text-xs text-red-500">
                        {passwordError}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      User will be required to change password on first login.
                    </p>
                  </div>
                </div>
                
                {error && (
                  <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
                    <p>{error}</p>
                  </div>
                )}
                
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => {
                      setUserDialogOpen(false);
                      setPasswordError(null);
                      setShowPassword(false);
                      setError(null);
                    }}
                    className="mr-2 px-4 py-2 text-sm font-medium text-night dark:text-white border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-night-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddUser}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-md shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Create User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Dialog */}
        {editUserDialogOpen && editingUser && (
          <div className="fixed inset-0 overflow-y-auto z-50 flex items-start justify-center p-4 bg-night-900/50 pt-8 pb-8">
            <div className="relative bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-lg w-full mx-auto my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-night-700">
                <h3 className="text-lg font-semibold text-night dark:text-white">Edit User</h3>
                <button 
                  onClick={() => setEditUserDialogOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Username</label>
                    <input
                      type="text"
                      value={editingUser.username}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Username cannot be changed
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Email</label>
                    <input
                      type="email"
                      value={editingUser.email}
                      onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Name</label>
                    <input
                      type="text"
                      value={editingUser.name}
                      onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={editingUser.phoneNumber || ''}
                      onChange={(e) => setEditingUser({...editingUser, phoneNumber: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      placeholder="Phone Number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Role</label>
                    <select
                      value={editingUser.role}
                      onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    >
                      {/* Admin can edit all roles */}
                      {user?.role === 'admin' && (
                        <>
                          <option value="tpa_admin">TPA Admin</option>
                          <option value="tpa_user">TPA User</option>
                          <option value="broker">Broker</option>
                          <option value="employer">Employer</option>
                        </>
                      )}
                      {/* TPA Admin can edit TPA users, broker and employer roles */}
                      {user?.role === 'tpa_admin' && (
                        <>
                          <option value="tpa_admin">TPA Admin</option>
                          <option value="tpa_user">TPA User</option>
                          <option value="broker">Broker</option>
                          <option value="employer">Employer</option>
                        </>
                      )}
                    </select>
                  </div>
                  
                  {/* TPA selection for global admins - can reassign to any TPA */}
                  {user?.role === 'admin' && (editingUser.role === 'tpa_admin' || editingUser.role === 'tpa_user' || editingUser.role === 'broker') && (
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">TPA</label>
                      <select
                        value={editingUser.tpaId || ''}
                        onChange={(e) => setEditingUser({...editingUser, tpaId: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      >
                        <option value="">Select TPA</option>
                        {loadingTpas ? (
                          <option value="" disabled>Loading TPAs...</option>
                        ) : (
                          tpaList.map((tpaItem) => (
                            <option key={tpaItem.id} value={tpaItem.id}>
                              {tpaItem.name}
                            </option>
                          ))
                        )}
                      </select>
                      {tpaList.length === 0 && !loadingTpas && (
                        <p className="mt-1 text-xs text-red-500">
                          No TPA groups found. Please create a TPA group first.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* TPA display for TPA admins - read-only, cannot reassign */}
                  {user?.role === 'tpa_admin' && (editingUser.role === 'tpa_admin' || editingUser.role === 'tpa_user' || editingUser.role === 'broker' || editingUser.role === 'employer') && (
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">TPA</label>
                      <input
                        type="text"
                        value={editingUser.tpaName || editingUser.tpaId || (user as any).tpaName || user.tpaId || 'Current TPA'}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        TPA admins can only edit users within their own TPA.
                      </p>
                    </div>
                  )}
                  
                  {/* Broker selection for employer users */}
                  {(editingUser?.role === 'employer') && (
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">Broker</label>
                      <select
                        value={editingUser.brokerId || ''}
                        onChange={(e) => {
                          const selectedBrokerId = e.target.value;
                          setEditingUser({
                            ...editingUser,
                            brokerId: selectedBrokerId
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      >
                        <option value="">Select Broker</option>
                        {loadingBrokers ? (
                          <option value="" disabled>Loading brokers...</option>
                        ) : (
                          brokerUsers.map((broker) => (
                            <option key={broker.username} value={broker.username}>
                              {broker.name || broker.username}
                            </option>
                          ))
                        )}
                      </select>
                      {brokerUsers.length === 0 && !loadingBrokers && (
                        <p className="mt-1 text-xs text-red-500">
                          No broker users found. Please create broker users first.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Agency field for broker users */}
                  {editingUser?.role === 'broker' && (
                    <div>
                      <label className="block text-sm font-medium text-night dark:text-white mb-1">Agency</label>
                      <input
                        type="text"
                        value={editingUser.agency || ''}
                        onChange={(e) => setEditingUser({...editingUser, agency: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                        placeholder="Agency Name (optional)"
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Password Management</label>
                    <button
                      type="button"
                      onClick={() => {
                        // In a real implementation, this would open a reset password dialog or call an API
                        alert(`Password reset for ${editingUser.username} would be implemented here`);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-amber-500 border border-transparent rounded-md shadow-sm hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                    >
                      Reset User Password
                    </button>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      This will send the user a password reset email
                    </p>
                  </div>
                </div>
                
                {error && (
                  <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
                    <p>{error}</p>
                  </div>
                )}
                
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setEditUserDialogOpen(false)}
                    className="mr-2 px-4 py-2 text-sm font-medium text-night dark:text-white border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-night-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateUser}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-md shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Update User
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TPA Group Creation Dialog */}
        {tpaDialogOpen && (
          <div className="fixed inset-0 overflow-y-auto z-50 flex items-start justify-center p-4 bg-night-900/50 pt-8 pb-8">
            <div className="relative bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-lg w-full mx-auto my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-night-700">
                <h3 className="text-lg font-semibold text-night dark:text-white">Create TPA Group</h3>
                <button 
                  onClick={() => setTpaDialogOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">TPA Name</label>
                    <input
                      type="text"
                      value={newTPA.name}
                      onChange={(e) => setNewTPA({...newTPA, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      placeholder="Enter Group Name"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Display name for the TPA group.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">TPA ID</label>
                    <input
                      type="text"
                      value={newTPA.id}
                      onChange={(e) => setNewTPA({...newTPA, id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                      placeholder="Enter Unique Identifier"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Lowercase, no spaces. This will be used as the TPA identifier.
                    </p>
                  </div>
                </div>
                
                {error && (
                  <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
                    <p>{error}</p>
                  </div>
                )}
                
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setTpaDialogOpen(false)}
                    className="mr-2 px-4 py-2 text-sm font-medium text-night dark:text-white border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-night-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateTPA}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-500 border border-transparent rounded-md shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Create TPA Group
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View User Dialog */}
        {viewUserDialogOpen && viewingUser && (
          <div className="fixed inset-0 overflow-y-auto z-50 flex items-start justify-center p-4 bg-night-900/50 pt-8 pb-8">
            <div className="relative bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-lg w-full mx-auto my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-night-700">
                <h3 className="text-lg font-semibold text-night dark:text-white">User Details</h3>
                <button 
                  onClick={() => setViewUserDialogOpen(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Username</label>
                    <input
                      type="text"
                      value={viewingUser.username}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Email</label>
                    <input
                      type="email"
                      value={viewingUser.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Name</label>
                    <input
                      type="text"
                      value={viewingUser.name}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Role</label>
                    <input
                      type="text"
                      value={viewingUser.role}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={viewingUser.phoneNumber || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                      placeholder="Phone Number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">TPA</label>
                    <input
                      type="text"
                      value={viewingUser.tpaName || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Broker</label>
                    <input
                      type="text"
                      value={viewingUser.brokerName || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Agency</label>
                    <input
                      type="text"
                      value={viewingUser.agency || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-1">Employer</label>
                    <input
                      type="text"
                      value={viewingUser.employerName || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm bg-gray-100 dark:bg-night-600 text-night dark:text-gray-400"
                    />
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setViewUserDialogOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-md shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Handle viewing a user
  const handleViewUser = (user: User) => {
    setViewingUser(user);
    setViewUserDialogOpen(true);
  };

  // Handle editing a user
  const handleEditUser = async (userToEdit: User) => {
    setEditingUser(userToEdit);
    setEditUserDialogOpen(true);
    setError(null);
    
    // Ensure TPA groups are loaded for admin users (check the logged-in user, not the user being edited)
    if (user?.role === 'admin') {
      console.log('Loading TPA groups for edit dialog');
      await fetchTpaGroups();
    }
    
    // If this is an employer user and they have a broker ID, 
    // fetch the employers for that broker
    if (userToEdit.role === 'employer' && userToEdit.brokerId) {
      fetchEmployersForBroker(userToEdit.brokerId);
    }
  };

  // Handle updating a user
  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      // Create update payload with only fields that have changed
      const updatePayload: Record<string, any> = {};

      // Only include fields that have values
      if (editingUser.email) updatePayload.email = editingUser.email;
      if (editingUser.name) updatePayload.name = editingUser.name;
      if (editingUser.phoneNumber) updatePayload.phoneNumber = editingUser.phoneNumber;
      if (editingUser.role) updatePayload.role = editingUser.role;

      // Update organization relationships
      if (editingUser.role === 'broker' || editingUser.role === 'tpa_admin' || editingUser.role === 'tpa_user') {
        if (editingUser.tpaId) updatePayload.tpaId = editingUser.tpaId;
      }

      // For TPA-level users (tpa_admin, tpa_user), don't set broker or employer IDs
      if (['tpa_admin', 'tpa_user'].includes(editingUser.role)) {
        // TPA-level users only need the TPA ID, no broker or employer associations
        console.log(`Updating TPA-level user (${editingUser.role}): ${editingUser.username}`);
      }
      // For broker users, always set broker_id to username
      else if (editingUser.role === 'broker') {
        updatePayload.brokerId = editingUser.username;
        console.log(`Setting broker_id to username for broker user: ${editingUser.username}`);
      }
      // For employer users, set employer_id to username and associate with broker
      else if (editingUser.role === 'employer') {
        // For employer users, always set employer_id to username
        updatePayload.employerId = editingUser.username;
        console.log(`Setting employer_id to username for employer user: ${editingUser.username}`);

        if (editingUser.brokerId) {
          updatePayload.brokerId = editingUser.brokerId;

          // Find the selected broker to get additional details if needed
          const selectedBroker = brokerUsers.find(b => b.username === editingUser.brokerId);
          if (selectedBroker) {
            console.log(`Selected broker: ${selectedBroker.name} (${selectedBroker.username})`);
            updatePayload.brokerName = selectedBroker.name || selectedBroker.username;
          }
        }
      }

      console.log('AdminPanel: Updating user with data:', updatePayload);

      // Use ServiceFactory to update user
      const result = await apiService.Users.updateUser(editingUser.username, updatePayload);
      console.log('AdminPanel: User updated successfully:', result);
      
      setEditUserDialogOpen(false);
      setError(null);
      
      // Immediately refresh the users list to show the updated user
      console.log('Refreshing users list after updating user');
      await fetchUsers();
      
      // Refresh broker users list if we updated a broker
      if (updatePayload.role === 'broker') {
        console.log('Refreshing broker users after updating broker');
        await fetchBrokerUsers();
      }
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.message || 'Failed to update user');
    }
  };

  // Fetch TPA groups
  const fetchTpaGroups = async () => {
    setLoadingTpas(true);
    try {
      if (!API_URL) {
        console.error('API URL not configured');
        return;
      }
      
      const token = await getIdToken();
      if (!token) {
        console.error('Authentication token not available');
        return;
      }
      
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_URL}/api/admin/tpa-groups?_t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${token.trim()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('TPA groups fetched successfully:', data);
        setTpaList(data.tpaGroups || []);
      } else {
        const errorText = await response.text();
        console.error(`Failed to fetch TPA groups: ${response.status} - ${errorText}`);
        setTpaList([]); // Clear the list on error
      }
    } catch (error) {
      console.error('Error fetching TPA groups:', error);
    } finally {
      setLoadingTpas(false);
    }
  };

  // Add state for success notification
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Create new TPA group
  const handleCreateTPA = async () => {
    if (!newTPA.id.trim() || !newTPA.name.trim()) {
      setError('TPA ID and name are required');
      return;
    }

    try {
      if (!API_URL) {
        setError('API URL not configured');
        return;
      }
      
      const token = await getIdToken();
      if (!token) {
        setError('Authentication token not available');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/admin/tpa-groups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token.trim()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: newTPA.id.toLowerCase().replace(/\s+/g, ''),
          name: newTPA.name
        })
      });

      if (response.ok) {
        setTpaDialogOpen(false);
        setNewTPA({ id: '', name: '' });
        setError(null);
        setSuccessMessage(`You've successfully created the TPA group "${newTPA.name}"`);
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
        
        await fetchTpaGroups(); // Refresh the list
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create TPA group');
      }
    } catch (error) {
      console.error('Error creating TPA group:', error);
      setError('Failed to create TPA group');
    }
  };

  // Main render logic with tabs
  return (
    <div className="space-y-6 w-full px-4">
      {/* Page header */}
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-night dark:text-white">Admin Panel</h1>
        <div className="flex gap-3">
          {/* White-label settings button */}
          {isFeatureEnabled('whiteLabel') && user?.role === 'admin' && (
            <WhiteLabelSettings />
          )}

          {/* Only show TPA group creation for admin users */}
          {user?.role === 'admin' && (
            <button
              onClick={() => setTpaDialogOpen(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-md shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Create TPA Group
            </button>
          )}
        </div>
      </div>

      {/* Success notification */}
      {successMessage && (
        <div className="mb-6 bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-md">
          <p>{successMessage}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-night-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`${
              activeTab === 'users'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <UserGroupIcon className="h-5 w-5 mr-2" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('organizations')}
            className={`${
              activeTab === 'organizations'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
          >
            <BuildingOfficeIcon className="h-5 w-5 mr-2" />
            Organizations
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'users' && renderUsers()}
      {activeTab === 'organizations' && <OrganizationManagement />}
    </div>
  );
  };

export default AdminPanel; 